/* eslint-disable react-refresh/only-export-components */
import { useRecordContext } from "ra-core";

import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { CustomFieldDef } from "../types";

/**
 * Tenant-defined custom fields (Settings → Custom fields).
 * Values live in the record's own `custom_fields` jsonb column, keyed by
 * slug - one generic column per object, identical across every tenant
 * (never a dynamic per-tenant column). Inputs use the dotted source
 * `custom_fields.<key>`, which react-hook-form/ra-core resolve as a nested
 * path on both read (lodash `get`) and write (submits a nested object).
 */

const sourceOf = (field: CustomFieldDef) => `custom_fields.${field.key ?? ""}`;

const valueOf = (record: unknown, field: CustomFieldDef) =>
  (record as { custom_fields?: Record<string, unknown> } | null)
    ?.custom_fields?.[field.key ?? ""];

const choicesOf = (field: CustomFieldDef) =>
  (field.options ?? "")
    .split(",")
    .map((o) => o.trim())
    .filter(Boolean)
    .map((o) => ({ id: o, name: o }));

/** Same options every existing currency display already uses (DealCard, DealShow). */
const formatCurrencyValue = (value: number, currency: string) =>
  value.toLocaleString(undefined, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
  });

const displayValueOf = (record: unknown, field: CustomFieldDef, currency: string) => {
  const v = valueOf(record, field);
  if (field.type === "number" && field.format === "currency" && typeof v === "number") {
    return formatCurrencyValue(v, currency);
  }
  return String(v);
};

export const useCustomFields = (
  object: CustomFieldDef["object"],
): CustomFieldDef[] => {
  const { customFields } = useConfigurationContext();
  return (customFields ?? []).filter((f) => f.object === object && f.key);
};

/** Form inputs for every custom field defined on this object type. */
export const CustomFieldsInput = ({
  object,
}: {
  object: CustomFieldDef["object"];
}) => {
  const fields = useCustomFields(object);
  if (fields.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-muted-foreground">
        Custom fields
      </h3>
      {fields.map((field) => {
        const source = sourceOf(field);
        switch (field.type) {
          case "number":
            return (
              <NumberInput
                key={source}
                source={source}
                label={field.label}
                helperText={field.format === "currency" ? "Enter a plain number - shown formatted as currency" : false}
              />
            );
          case "date":
            return (
              <DateInput key={source} source={source} label={field.label} helperText={false} />
            );
          case "select":
            return (
              <SelectInput
                key={source}
                source={source}
                label={field.label}
                choices={choicesOf(field)}
                helperText={false}
              />
            );
          default:
            return (
              <TextInput key={source} source={source} label={field.label} helperText={false} />
            );
        }
      })}
    </div>
  );
};

/** Read-only list of populated custom field values for show views/asides. */
export const CustomFieldsDisplay = ({
  object,
}: {
  object: CustomFieldDef["object"];
}) => {
  const fields = useCustomFields(object);
  const { currency } = useConfigurationContext();
  const record = useRecordContext();
  if (!record || fields.length === 0) return null;
  const populated = fields.filter((f) => {
    const v = valueOf(record, f);
    return v != null && v !== "";
  });
  if (populated.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-sm font-medium text-muted-foreground">
        Custom fields
      </h3>
      {populated.map((f) => (
        <p key={sourceOf(f)} className="text-sm">
          <span className="text-muted-foreground">{f.label}: </span>
          {displayValueOf(record, f, currency)}
        </p>
      ))}
    </div>
  );
};
